import { Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { StaticImage } from 'gatsby-plugin-image'
import { get } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'

import { NO_DATA_MESSAGE } from '../../utils/constants'
import AsOfDate from '../AsOfDate'
import ScrollingTable from '../Tables/ScrollingTable'

// Styled components
const SectorNameContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  borderTop: 'none',
}))

const EconomicSectorIcon = ({ sector }) => {
  switch (sector) {
    case 'Communication Services':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/communicationServices.svg"
        />
      )
    case 'Consumer Discretionary':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/consumerDiscretionary.svg"
        />
      )
    case 'Consumer Staples':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/consumerStaples.svg"
        />
      )
    case 'Energy':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/energy.svg"
        />
      )
    case 'Financials':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/financials.svg"
        />
      )
    case 'Health Care':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/healthCare.svg"
        />
      )
    case 'Industrials':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/industrials.svg"
        />
      )
    case 'Information Technology':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/informationTechnology.svg"
        />
      )
    case 'Materials':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/materials.svg"
        />
      )
    case 'Real Estate':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/realEstate.svg"
        />
      )
    case 'Utilities':
      return (
        <StaticImage
          alt="Economic sector icon"
          src="../../assets/images/icons/economicSectors/utilities.svg"
        />
      )
    default:
      return <div />
  }
}

EconomicSectorIcon.propTypes = {
  sector: PropTypes.string.isRequired,
}

const createOptions = (presentation) => ({ presentation })

const SectorReturns = ({ data, presentation }) => {
  const options = useMemo(() => createOptions(presentation), [presentation])
  const sectorReturns = Array.isArray(data?.sectorReturns)
    ? data.sectorReturns
    : []
  const asOfDate =
    sectorReturns.length > 0 ? get(sectorReturns[0], 'calendar.date', '') : ''

  const fundName = get(data, 'name', 'Fund')

  const valueRenderer = (props) => (
    <span>
      {typeof props.value === 'number'
        ? parseFloat(props.value).toFixed(1)
        : '--'}
    </span>
  )

  const tableData = useMemo(() => {
    const rows = []
    sectorReturns?.forEach((item) => {
      const row = { name: item.name, fund: item.fundValue || '--' }
      if (item.benchmarkValue) {
        row[`benchmark-0`] = item.benchmarkValue
      }
      rows.push(row)
    })
    return rows
  }, [sectorReturns])

  const columns = useMemo(() => {
    const cols = [
      {
        Header: 'Economic Sector',
        id: 'economicSector',
        accessor: 'name',
        Cell: ({ value }) => (
          <SectorNameContainer>
            <Typography variant="body2">{value}</Typography>
            <EconomicSectorIcon sector={value} />
          </SectorNameContainer>
        ),
      },
      {
        Header: `${fundName} %`,
        id: 'fundName',
        accessor: 'fund',
        Cell: valueRenderer,
      },
    ]

    if (sectorReturns.length > 0 && sectorReturns[0].benchmarkIndex) {
      cols.push({
        Header: `${sectorReturns[0].benchmarkIndex.name} %`,
        id: 'benchmark-0',
        accessor: `benchmark-0`,
        Cell: valueRenderer,
      })
    }

    return cols
  }, [sectorReturns, fundName])

  if (tableData.length === 0) {
    return <p>{NO_DATA_MESSAGE}</p>
  }

  const columnWidth = `${100 / columns.length}%`

  return (
    <>
      <AsOfDate date={asOfDate} />
      <ScrollingTable
        data={tableData}
        columns={columns}
        options={options}
        Col1Width={columnWidth}
      />
    </>
  )
}

SectorReturns.propTypes = {
  data: PropTypes.shape({
    sectorReturns: PropTypes.array,
    name: PropTypes.string,
  }),
  presentation: PropTypes.object,
}

export default SectorReturns
